import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Fuel, Crown } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { SiteFooter } from "../components/SiteFooter";
import { PageHero } from "../components/PageHero";
import { PriceBoard } from "../components/PriceBoard";
import { AUTO_WASH, QUICK_WASH, AUTO_WASH_STEPS, AUTO_WASH_HOURS, DOG_WASH, SELF_SERVE, FUEL_DISCOUNT, VACUUM, MAT_CLEANER } from "../lib/business";
import api from "../lib/api";

function SectionHead({ eyebrow, title, to, testid }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-8">
      <div>
        <div className="overline text-volt mb-2">{eyebrow}</div>
        <h2 className="font-head font-extrabold text-3xl md:text-4xl uppercase tracking-tight">{title}</h2>
      </div>
      {to && (
        <Link to={to} data-testid={testid} className="overline text-muted-foreground flex items-center gap-1 hover:text-white transition-colors">
          More <ArrowRight className="w-3 h-3 text-volt" />
        </Link>
      )}
    </div>
  );
}

function MembersBoard({ price }) {
  return (
    <div className="relative border border-volt bg-volt/10 p-8 grid md:grid-cols-[1fr_auto] gap-6 items-center" data-testid="pricing-members-board">
      <div>
        <div className="overline text-volt mb-3 flex items-center gap-2"><Crown className="w-4 h-4" /> Members Only — Auto Wash</div>
        <h3 className="font-head font-black text-3xl md:text-4xl uppercase tracking-tight">Unlimited Auto Washes</h3>
        <p className="text-sm text-muted-foreground mt-3 tracking-wide max-w-xl">
          One flat monthly price per number plate. Each extra car 10% off. Membership covers the automatic car wash only — self-serve bays and dog wash are pay as you go.
        </p>
      </div>
      <div className="flex flex-col items-start md:items-end gap-4">
        <div className="flex items-baseline gap-1">
          <span className="font-head font-black text-5xl" data-testid="pricing-members-price">${price}</span>
          <span className="overline text-muted-foreground">/ month</span>
        </div>
        <Link to="/register" data-testid="pricing-members-join-btn" className="inline-flex items-center gap-2 bg-volt text-white px-6 py-3 font-head font-bold uppercase tracking-wide hover:bg-blue-600 transition-colors">
          Join Now <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

export default function Pricing() {
  const [price, setPrice] = useState(49.95);
  useEffect(() => { api.get("/config").then((r) => r.data?.price && setPrice(r.data.price)).catch(() => {}); }, []);
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  }, [hash]);
  const slug = (n) => n.toLowerCase().replace(/\s/g, "-");

  return (
    <div className="min-h-screen bg-background grain">
      <Navbar />
      <PageHero testid="pricing-hero" eyebrow="Pricing" title="Our Prices" text="Auto wash, members, self-serve bays and dog wash — all in one place." />

      <section id="auto-wash" className="mx-auto max-w-7xl px-6 pt-20 scroll-mt-20">
        <SectionHead eyebrow={AUTO_WASH_HOURS} title="Auto Car Wash Menu" to="/membership" testid="pricing-auto-link" />
        <div className="border border-white/10 p-4 mb-10 flex items-center gap-3" data-testid="pricing-fuel-discount">
          <Fuel className="w-5 h-5 text-volt shrink-0" />
          <span className="font-head font-bold uppercase tracking-tight">{FUEL_DISCOUNT}</span>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {AUTO_WASH.map((w) => <PriceBoard key={w.name} {...w} testid={`pricing-board-${slug(w.name)}`} />)}
        </div>
        <div className="mt-6 grid lg:grid-cols-4 gap-6">
          <PriceBoard {...QUICK_WASH} testid="pricing-board-quick-wash" />
          <div id="how-to-use" className="scroll-mt-24 lg:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10" data-testid="auto-wash-steps">
            {AUTO_WASH_STEPS.map((t, i) => (
              <div key={t} className="bg-background p-6 flex flex-col justify-center">
                <div className="font-head font-black text-4xl text-volt/40 mb-2">0{i + 1}</div>
                <div className="font-head font-bold text-lg uppercase tracking-tight">{t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pt-16">
        <MembersBoard price={price} />
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 grid md:grid-cols-2 gap-12">
        <div id="self-serve" className="scroll-mt-24">
          <SectionHead eyebrow={`Open ${SELF_SERVE.hours}`} title="Self-Serve Bays" to="/self-serve" testid="pricing-self-serve-link" />
          <PriceBoard name="Self-Serve Bay" price={SELF_SERVE.from} sub={`From · ${SELF_SERVE.per}`} features={[...SELF_SERVE.features, "Time based pricing"]} testid="pricing-board-self-serve" />
          <div id="vacuum" className="mt-6 scroll-mt-24"><PriceBoard {...VACUUM} testid="pricing-board-vacuum" /></div>
          <div id="mat-cleaner" className="mt-6 scroll-mt-24"><PriceBoard {...MAT_CLEANER} testid="pricing-board-mat-cleaner" /></div>
        </div>
        <div id="dog-wash" className="scroll-mt-24">
          <SectionHead eyebrow="Open 24/7" title="Dog Wash" to="/dog-wash" testid="pricing-dog-wash-link" />
          <PriceBoard {...DOG_WASH} testid="pricing-board-dog-wash" />
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
